import {
	AccountData,
	SetPermmissionSubmission,
	AddRemoveAccountUserSubmission,
	PermissionsData,
	permissionsGroups,
	PermissionsGroup,
	GdbPathMinimum,
	GdbPath,
	CommandData,
	UserDoc,
	DownloadFormat,
} from './models.def';
import fb from './gobi-mw-firestore';
import store from '@/store';
import firebase from 'firebase';
import accountx from './modules/accountx';
import userx from './modules/userx';
import { download } from '@/util/download';
import { Dictionary } from 'vue-router/types/router';
import JSZip from 'jszip';

const db: firebase.firestore.Firestore = fb.db;
const functions: firebase.functions.Functions = fb.functions;

export class Accounts {


	public currentAccountId: string | null = null;

	private _unsubscribeListenToCurrentAccount: (() => void) | null = null;
	private _unsubscribeListenToCurrentAccountPermissions: (() => void) | null = null;
	private _unsubscribeListenToCurrentAccountCommands: (() => void) | null = null;

	constructor() {

		store.watch(
			(states: any, getters) => states.accountx && states.accountx.currentAccountId,
			(newValue: string, oldValue: string) => {
				console.log(this.currentAccountId, newValue);
				if (this.currentAccountId !== newValue) {
					if (newValue) {

						if (this._unsubscribeListenToCurrentAccount) {
							this._unsubscribeListenToCurrentAccount();
						}
						if (this._unsubscribeListenToCurrentAccountPermissions) {
							this._unsubscribeListenToCurrentAccountPermissions();
						}
						if (this._unsubscribeListenToCurrentAccountCommands) {
							this._unsubscribeListenToCurrentAccountCommands();
						}
						this.listenToCurrentAccount(newValue);
					}
				}
			});

		// Clear account data on logout
		store.watch(
			(states, getters) => getters['userx/supportLvl'],
			(newValue, oldValue) => {
				if (!newValue) {
					this.cleanup();
				}
			});

		(window as any).accountz = this;
	}

	public cleanup() {
		if (this._unsubscribeListenToCurrentAccount) {
			this._unsubscribeListenToCurrentAccount();
			this._unsubscribeListenToCurrentAccount = null;
		}
		if (this._unsubscribeListenToCurrentAccountPermissions) {
			this._unsubscribeListenToCurrentAccountPermissions();
			this._unsubscribeListenToCurrentAccountPermissions = null;
		}
		if (this._unsubscribeListenToCurrentAccountCommands) {
			this._unsubscribeListenToCurrentAccountCommands();
			this._unsubscribeListenToCurrentAccountCommands = null;
		}
		this.currentAccountId = null;
		accountx.updateCurrentAccount(null);
		accountx.updateCurrentAccountPermissions({});
		accountx.updateCurrentAccountCommands([]);
		accountx.updateCurrentAccountLastLogins({});
	}


	public listenToCurrentAccount(accountId: string) {
		console.log('read accountData', accountId);
		this.currentAccountId = accountId;
		this._unsubscribeListenToCurrentAccount = db.doc(`accounts/${accountId}`)
			.onSnapshot((doc) => {
				if (!doc.exists) {
					accountx.updateCurrentAccount(null);
					return;
				}

				const accountData = doc.data() as AccountData;

				accountx.updateCurrentAccount(accountData);
			});
		this._unsubscribeListenToCurrentAccountPermissions = db.doc(`accounts/${accountId}/permissions/users`)
			.onSnapshot(async (doc) => {
				if (!doc.exists) {
					accountx.updateCurrentAccountPermissions({});
					return;
				}

				const permissions = doc.data() as PermissionsData;
				delete permissions.emails;
				const uids: string[] = [];
				for (const permission of Object.values(permissions)) {
					if (permission.uid) { uids.push(permission.uid); }
					if (permission.admin) {
						for (const key of permissionsGroups) {
							if (key !== 'global') {
								permission[key] = true;
							}
						}
					}
				}

				accountx.updateCurrentAccountPermissions(permissions);
				const promises = uids.map((uid) => db.doc(`users/${uid}`).get());
				const docs = await Promise.all(promises);
				const lastLogins: Dictionary<number> = {};
				docs.forEach((userDoc) => {
					const data = userDoc.data() as UserDoc;
					if (data.lastLoginTime) {
						lastLogins[data.email] = data.lastLoginTime;
					}
				});
				accountx.updateCurrentAccountLastLogins(lastLogins);
			});
		this._unsubscribeListenToCurrentAccountCommands = db.collection('commands')
			.where('accountId', '==', accountId).orderBy('inactive').orderBy('issuedAt', 'desc').limit(10)
			.onSnapshot((docs) => {
				if (docs.empty) {
					accountx.updateCurrentAccountCommands([]);
				}

				const commands = docs.docs.map((doc) => doc.data() as CommandData);

				if (userx.supportLvl && userx.supportLvl >= 10) {
					docs.docs.forEach((doc) => {
						const command = doc.data() as CommandData;
						if (!command.inactive && command.status === 'interrupted') {
							doc.ref.update({ inactive: true });
						}
					});
				}

				accountx.updateCurrentAccountCommands(commands);
			});
	}

	public async setPermissions(submission: SetPermmissionSubmission) {
		const { accountId, email } = submission;
		const permissions: string[] = permissionsGroups.slice(1);
		permissions.unshift('admin');

		const permission: any = {};
		for (const key of permissions) {

			permission[key] = (submission.admin) ? true : (submission as any)[key] || false;
		}

		const update: any = {};
		update[email] = permission;

		try {

			await db.doc(`accounts/${accountId}/permissions/users`).set(update, { merge: true });
			return { success: true };
		} catch (error: any) {
			console.error(error);
			return { success: false, error, errorCode: error.code, errorMessage: error.message };
		}
	}

	public async addRemoveAccountUser(submission: AddRemoveAccountUserSubmission) {
		const assert = fb.assertSupportLvl(3);
		if (!assert.success) { return assert; }
		const { remove, email } = submission;
		if (!remove) {
			this.inviteNewAccountUser(email);
		}
		try {
			const result = await functions
				.httpsCallable('addRemoveAccountUser')(submission);
			return { success: true };
		} catch (error: any) {
			console.error(error);
			return { success: false, error, errorCode: error.code, errorMessage: error.message };
		}
	}

	public async inviteNewAccountUser(email: string) {
		const isUserExist = await fb.isUserExist(email);
		// if (!isUserExist && !userx.recentInvitedEmails.has(email)) {
		// 	console.log('send invitation to', email);
		// 	userx.sendSignInEmail(email);
		// }
	}

	public async downloadZip(group: PermissionsGroup, format: DownloadFormat = 'csv') {
		const accountId = accountx.currentAccountId;
		const accountData = accountx.currentAccount as any;
		if (!accountData) { return null; }
		const gdbPath = accountData[`${group}LastAvailable`] as GdbPathMinimum | undefined;
		if (!gdbPath) { return null; }
		try {
			const doc = await db.doc(`/accounts/${accountId}/${group}/${gdbPath.gdbid}`).get();
			const fullGdbPath: GdbPath = doc.data() as GdbPath;

			// New approach: create ZIP from individual CSVs/Parquets
			if (fullGdbPath.csvs && Object.keys(fullGdbPath.csvs).length > 0) {
				const zip = new JSZip();

				for (const [csvName, csvEntry] of Object.entries(fullGdbPath.csvs)) {
					try {
						if (format === 'csv' || format === 'both') {
							const response = await fetch(csvEntry.downloadUrl);
							const blob = await response.blob();
							zip.file(`${csvName}.csv`, blob);
						}
						if ((format === 'parquet' || format === 'both') && csvEntry.parquetUrl) {
							const response = await fetch(csvEntry.parquetUrl);
							const blob = await response.blob();
							zip.file(`${csvName}.parquet`, blob);
						}
					} catch (err) {
						console.error(`Failed to fetch ${csvName}:`, err);
					}
				}

				const finalBlob = await zip.generateAsync({ type: 'blob' });
				const url = URL.createObjectURL(finalBlob);
				const a = document.createElement('a');
				a.href = url;
				a.download = `${accountId}_${group}_${format}.zip`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
				return true;
			}

			// Fallback: use legacy downloadUrl if available (for old data)
			const { downloadUrl } = fullGdbPath;
			if (downloadUrl) {
				download(downloadUrl, `${accountId}_${group}.zip`);
				return downloadUrl;
			}

			return null;
		} catch (error) {
			console.error('Failed to load:', `/accounts/${accountId}/${group}/${gdbPath.gdbid}`, error);
		}
	}

	public async downloadAllZips(
		format: DownloadFormat = 'csv',
		onProgress?: (current: number, total: number, group: string) => void,
	) {
		const accountId = accountx.currentAccountId;
		const accountData = accountx.currentAccount as any;
		if (!accountData || !accountId) { return null; }

		const combinedZip = new JSZip();
		const availableGroups: Array<{ group: PermissionsGroup; fullGdbPath: GdbPath }> = [];

		// First, collect all available groups with their data
		for (const group of permissionsGroups) {
			const gdbPath = accountData[`${group}LastAvailable`] as GdbPathMinimum | undefined;
			if (!gdbPath) { continue; }

			try {
				const doc = await db.doc(`/accounts/${accountId}/${group}/${gdbPath.gdbid}`).get();
				const fullGdbPath: GdbPath = doc.data() as GdbPath;
				if (fullGdbPath && (fullGdbPath.csvs || fullGdbPath.downloadUrl)) {
					availableGroups.push({ group, fullGdbPath });
				}
			} catch (error) {
				console.error('Failed to load:', `/accounts/${accountId}/${group}/${gdbPath.gdbid}`, error);
			}
		}

		if (availableGroups.length === 0) { return null; }

		// Process each group
		for (let i = 0; i < availableGroups.length; i++) {
			const { group, fullGdbPath } = availableGroups[i];
			if (onProgress) { onProgress(i + 1, availableGroups.length, group); }

			const folder = combinedZip.folder(group);
			if (!folder) { continue; }

			try {
				// New approach: fetch individual CSVs/Parquets
				if (fullGdbPath.csvs && Object.keys(fullGdbPath.csvs).length > 0) {
					for (const [csvName, csvEntry] of Object.entries(fullGdbPath.csvs)) {
						try {
							if (format === 'csv' || format === 'both') {
								const response = await fetch(csvEntry.downloadUrl);
								const blob = await response.blob();
								folder.file(`${csvName}.csv`, blob);
							}
							if ((format === 'parquet' || format === 'both') && csvEntry.parquetUrl) {
								const response = await fetch(csvEntry.parquetUrl);
								const blob = await response.blob();
								folder.file(`${csvName}.parquet`, blob);
							}
						} catch (err) {
							console.error(`Failed to fetch ${csvName}:`, err);
						}
					}
				} else if (fullGdbPath.downloadUrl) {
					// Fallback: use legacy ZIP downloadUrl (for old data)
					const response = await fetch(fullGdbPath.downloadUrl);
					const zipBlob = await response.blob();
					const groupZip = await JSZip.loadAsync(zipBlob);

					for (const [filename, file] of Object.entries(groupZip.files)) {
						if (!file.dir) {
							const content = await file.async('blob');
							folder.file(filename, content);
						}
					}
				}
			} catch (error) {
				console.error(`Failed to process ${group}:`, error);
			}
		}

		// Generate final combined zip
		const finalBlob = await combinedZip.generateAsync({ type: 'blob' });

		// Trigger download
		const url = URL.createObjectURL(finalBlob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `${accountId}_all_${format}.zip`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);

		return true;
	}
}
const mwz = new Accounts();
export default mwz;
